import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePlayer } from "../context/PlayerContext";
import { isAuthenticated } from "../lib/auth";
import api from "../services/api";
import AddToPlaylistButton from "./AddToPlaylistButton";

// Carte station réutilisable (Explore, Favoris, Historique, Playlist).
// Props :
//  - station : { station_uuid, name, url, favicon, country, tags, bitrate }
//  - onRemove : fn optionnelle -> affiche un bouton ✕ (retrait)
//  - allowFavorite / allowPlaylist : booléens (défaut true)
export default function StationCard({ station, onRemove, allowFavorite = true, allowPlaylist = true }) {
  const { t } = useTranslation();
  const player = usePlayer();
  const navigate = useNavigate();
  const [faved, setFaved] = useState(false);
  const isCurrent = player?.current?.station_uuid === station.station_uuid;
  const isPlaying = isCurrent && player?.playing;

  const requireAuth = () => {
    if (!isAuthenticated()) { navigate("/signin"); return false; }
    return true;
  };

  const addFavorite = async () => {
    if (!requireAuth()) return;
    try {
      await api.post("/api/favorites", {
        station_uuid: station.station_uuid,
        name: station.name,
        url: station.url,
        favicon: station.favicon,
        country: station.country,
        tags: station.tags,
      });
      setFaved(true);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
      <img
        src={station.favicon || "/microphone.png"}
        alt=""
        className="w-14 h-14 rounded-xl object-cover bg-gray-100 flex-shrink-0"
        onError={(e) => { e.currentTarget.src = "/microphone.png"; }}
      />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900 truncate">{station.name}</div>
        <div className="text-xs text-gray-500 truncate">
          {[station.country, station.tags, station.bitrate ? station.bitrate + "kbps" : null]
            .filter(Boolean)
            .join(" • ")}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => player?.play(station)}
          disabled={!station.url}
          title={station.url ? t("stationCard.play") : t("stationCard.noStream")}
          className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${
            station.url ? "bg-purple-600 hover:bg-purple-700" : "bg-gray-300 cursor-not-allowed"
          }`}
        >
          {isPlaying ? "❚❚" : "►"}
        </button>

        {allowFavorite && (
          <button
            onClick={addFavorite}
            title={t("stationCard.addFavorite")}
            className={`w-9 h-9 rounded-full flex items-center justify-center ${
              faved ? "bg-pink-100 text-pink-600" : "bg-gray-100 hover:bg-gray-200 text-gray-600"
            }`}
          >
            {faved ? "♥" : "♡"}
          </button>
        )}

        {allowPlaylist && isAuthenticated() && <AddToPlaylistButton station={station} />}

        {onRemove && (
          <button
            onClick={() => onRemove(station)}
            title={t("stationCard.remove")}
            className="w-9 h-9 rounded-full bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-600 flex items-center justify-center"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
